import { useEffect, useRef, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon } from '@/components/Icon'
import { columnLabel } from './columnLabel'
import { describeValue } from './FilterBuilder'
import { chipActive, sameFilter, withChip, withoutChip, type QuickFilter } from './quickFilters'
import type { ColumnDescriptor, FilterSpec } from './types'
import '@/styles/lists.css'

/**
 * Части тулбара списка (дизайн: CompData — «Тулбар списка — один ряд»).
 *
 * Всё здесь — правка одного и того же массива `filters` из состояния списка: поиск, чипы,
 * токены и конструктор в поповере не держат своих фильтров. Иначе на экране было бы два
 * ответа на вопрос «что сейчас отфильтровано», и токены показывали бы не то, что ушло на
 * сервер.
 */

/** Чип с уже собранными условиями — см. `availableChips`. */
export interface ResolvedChip {
  chip: QuickFilter
  filters: FilterSpec[]
}

// ------------------------------------------------------------------- поиск

/**
 * Поиск по списку — условие `contains` по одной текстовой колонке.
 *
 * Сервер ищет условиями механизма списков, а условия соединяются только «И»: поиска «по
 * сделке, клиенту и номеру сразу» он не умеет. Поэтому поле честно называет колонку, по
 * которой ищет, а не обещает искать везде и молча находить по одной.
 */
export function SearchBox({
  column,
  descriptors,
  filters,
  onChange,
}: {
  column: string
  descriptors: ColumnDescriptor[]
  filters: FilterSpec[]
  onChange: (filters: FilterSpec[]) => void
}) {
  const { t } = useApp()
  const input = useRef<HTMLInputElement>(null)
  const isSearch = (filter: FilterSpec) => filter.column === column && filter.operator === 'contains'
  const applied = String(filters.find(isSearch)?.value ?? '')
  const [draft, setDraft] = useState(applied)

  // Условие сняли токеном или «Сбросить всё» — поле обязано опустеть вместе с ним, иначе
  // в поле стоит текст, по которому список уже не фильтруется.
  useEffect(() => setDraft(applied), [applied])

  const apply = (text: string) => {
    const value = text.trim()
    if (value === applied) return
    const rest = filters.filter((filter) => !isSearch(filter))
    onChange(value ? [...rest, { column, operator: 'contains', value }] : rest)
  }

  // Набор применяется сам после паузы: Enter — для тех, кто ждать не хочет. Без паузы
  // каждая буква была бы запросом к серверу.
  useEffect(() => {
    if (draft.trim() === applied) return
    const timer = window.setTimeout(() => apply(draft), 400)
    return () => window.clearTimeout(timer)
    // apply пересоздаётся на каждой отрисовке; таймер зависит от текста, а не от функции.
  }, [draft, applied])

  // «/» — к поиску (дизайн: хоткеи). В поле ввода это символ, а не команда: иначе дробь
  // в комментарии не набрать.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) return
      const target = event.target as HTMLElement | null
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable === true)
      ) {
        return
      }
      event.preventDefault()
      input.current?.focus()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const label = t('list.searchIn', { column: columnLabel(t, descriptors, column) })
  return (
    <div className="input-icon list-search">
      <Icon name="search" />
      <input
        ref={input}
        className="input input-sm"
        type="search"
        aria-label={label}
        placeholder={label}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') apply(draft)
          if (event.key === 'Escape') {
            setDraft('')
            apply('')
          }
        }}
      />
      <span className="kbd" aria-hidden="true">
        /
      </span>
    </div>
  )
}

// -------------------------------------------------------------- быстрые чипы

export function QuickChips({
  chips,
  filters,
  onChange,
}: {
  chips: ResolvedChip[]
  filters: FilterSpec[]
  onChange: (filters: FilterSpec[]) => void
}) {
  const { t } = useApp()
  if (chips.length === 0) return null
  return (
    <div className="row-wrap list-chips" role="group" aria-label={t('quick.label')}>
      {chips.map(({ chip, filters: own }) => {
        const active = chipActive(own, filters)
        const others = chips
          .filter((other) => other.chip.key !== chip.key && chipActive(other.filters, filters))
          .map((other) => other.filters)
        return (
          <button
            key={chip.key}
            type="button"
            className="chip"
            aria-pressed={active}
            onClick={() => onChange(active ? withoutChip(own, filters, others) : withChip(own, filters))}
          >
            {active && <Icon name="check" />}
            {t(chip.labelKey)}
          </button>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------- токены условий

/**
 * Применённые условия токенами «колонка · оператор · значение» (дизайн: `.ftoken`).
 *
 * Отвечают на вопрос «почему строк столько» и дают снять одно условие, не открывая
 * конструктор. Условия включённого чипа показываются одним токеном с его названием:
 * «Просроченные» понятнее, чем «Статус равно Открыта» и «Срок раньше 09.10.2026», и
 * снимаются они тоже вместе.
 */
export function FilterTokens({
  entity,
  descriptors,
  filters,
  chips,
  onChange,
  extra,
}: {
  entity: string
  descriptors: ColumnDescriptor[]
  filters: FilterSpec[]
  chips: ResolvedChip[]
  onChange: (filters: FilterSpec[]) => void
  /** Действия справа от токенов: сохранить как представление и т.п. */
  extra?: React.ReactNode
}) {
  const { t } = useApp()
  if (filters.length === 0) return null

  // Чип, чьи условия целиком входят в другой включённый (у счетов «Неоплаченные» внутри
  // «Просроченных»), отдельным токеном не показывается: один набор условий не должен
  // читаться двумя причинами.
  const allActive = chips.filter((chip) => chipActive(chip.filters, filters))
  const active = allActive.filter(
    (chip) =>
      !allActive.some(
        (other) =>
          other.chip.key !== chip.chip.key &&
          other.filters.length > chip.filters.length &&
          chipActive(chip.filters, other.filters),
      ),
  )
  const loose = filters
    .map((filter, index) => ({ filter, index }))
    .filter(({ filter }) => !active.some((chip) => chip.filters.some((spec) => sameFilter(spec, filter))))

  return (
    <div className="row-wrap list-tokens" aria-label={t('filter.applied')} role="group">
      <span className="t-sm muted">{t('filter.applied')}:</span>
      {active.map(({ chip, filters: own }) => (
        <span key={`chip-${chip.key}`} className="ftoken">
          <span className="k">{t('quick.token')}</span>
          <span className="op" />
          <span className="v">{t(chip.labelKey)}</span>
          <button
            type="button"
            aria-label={t('filter.removeToken', { column: t(chip.labelKey) })}
            onClick={() =>
              onChange(
                withoutChip(
                  own,
                  filters,
                  active.filter((other) => other.chip.key !== chip.key).map((other) => other.filters),
                ),
              )
            }
          >
            <Icon name="close" />
          </button>
        </span>
      ))}
      {loose.map(({ filter, index }) => {
        const label = columnLabel(t, descriptors, filter.column)
        return (
          <span key={`${filter.column}-${index}`} className="ftoken">
            <span className="k">{label}</span>
            <span className="op">{t(`filter.op.${filter.operator}`)}</span>
            <span className="v">{describeValue(filter, entity, t)}</span>
            <button
              type="button"
              aria-label={t('filter.removeToken', { column: label })}
              onClick={() => onChange(filters.filter((_, i) => i !== index))}
            >
              <Icon name="close" />
            </button>
          </span>
        )
      })}
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange([])}>
        {t('filter.clearAll')}
      </button>
      {extra}
    </div>
  )
}
